import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkle, PaperPlaneRight, Folder, Books, FileText, Files as FilesIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { folderOptions } from './folderTree';
import { dmsApi } from './dmsApi';

export default function AskScopeDialog({ open, onClose, folders, onOpenDoc }) {
  const [scopeType, setScopeType] = useState('folder');
  const [scopeId, setScopeId] = useState('');
  const [bundles, setBundles] = useState([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && scopeType === 'bundle' && bundles.length === 0) {
      dmsApi.bundles().then(setBundles).catch(() => setBundles([]));
    }
  }, [open, scopeType, bundles.length]);

  const targets = scopeType === 'folder'
    ? folderOptions(folders.filter((f) => f.seed_key !== 'dms_scan_inbox'))
      .map((o) => ({ id: o.value, name: o.pathLabel }))
    : bundles.map((b) => ({ id: b.id, name: b.name }));

  const send = async () => {
    if (scopeType !== 'all' && !scopeId) { toast.error('対象を選択してください'); return; }
    if (!question.trim()) { toast.error('質問を入力してください'); return; }
    setBusy(true); setResult(null);
    try {
      setResult(await dmsApi.askScope(question.trim(), scopeType, scopeType === 'all' ? 'all' : scopeId));
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI応答の取得に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="dms-askscope-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Sparkle size={17} weight="duotone" className="text-violet-600" /> まとめて質問
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-zinc-500">フォルダーまたはバインダー内の読み取り済みの書類をまとめてAIに質問できます。回答には根拠の書類番号が含まれます。</p>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-zinc-200 overflow-hidden shrink-0" data-testid="askscope-type">
            {[['folder', Folder, 'フォルダー'], ['bundle', Books, 'バインダー'], ['all', FilesIcon, 'すべて']].map(([t, IconC, label]) => (
              <button key={t} type="button" onClick={() => { setScopeType(t); setScopeId(''); }}
                data-testid={`askscope-type-${t}`}
                className={`flex items-center gap-1 px-2.5 py-2 text-[11px] font-semibold ${
                  scopeType === t ? 'bg-violet-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                <IconC size={13} weight="duotone" /> {label}
              </button>
            ))}
          </div>
          {scopeType !== 'all' ? (
            <Select value={scopeId} onValueChange={setScopeId}>
              <SelectTrigger className="h-9 text-xs flex-1" data-testid="askscope-target">
                <SelectValue placeholder={scopeType === 'folder' ? 'フォルダーを選択' : 'バインダーを選択'} />
              </SelectTrigger>
              <SelectContent>
                {targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <p className="flex-1 text-[11px] text-zinc-500" data-testid="askscope-all-hint">
              閲覧できる読み取り済みの書類すべてを横断検索します（RAG索引があれば関連箇所を自動抽出）
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder="例: 合計金額が最も大きい書類はどれ？" className="h-9 text-sm flex-1"
            onKeyDown={(e) => e.key === 'Enter' && !busy && send()} data-testid="askscope-question" />
          <Button onClick={send} disabled={busy} aria-label="質問する" title="質問する"
            className="h-9 w-9 p-0 bg-teal-700 hover:bg-teal-800" data-testid="askscope-send">
            <PaperPlaneRight size={15} />
          </Button>
        </div>
        {busy && <p className="text-xs text-zinc-400 py-2 text-center animate-pulse">AIが書類を読んでいます…</p>}
        {result && (
          <div className="space-y-2">
            <div className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2.5 text-[13px] text-zinc-800 whitespace-pre-wrap max-h-64 overflow-y-auto"
              data-testid="askscope-answer">{result.answer}</div>
            <div className="space-y-1">
              <p className="text-[10px] font-bold text-zinc-400 uppercase">参照した書類（{result.used_documents.length}件）</p>
              {result.used_documents.map((d) => (
                <button key={d.id} type="button" onClick={() => onOpenDoc(d.id)}
                  data-testid={`askscope-used-${d.id}`}
                  className="w-full flex items-center gap-1.5 rounded-md border border-zinc-200 px-2 py-1.5 text-left text-[11px] text-zinc-600 hover:bg-violet-50 hover:text-violet-800 transition-colors">
                  <FileText size={13} weight="duotone" className="text-zinc-400 shrink-0" />
                  <span className="shrink-0 rounded border border-violet-100 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-600">書類</span>
                  <span className="truncate flex-1">{d.title}</span>
                  <span className="font-mono text-[9px] text-zinc-400">{d.document_number}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
